import { Button } from '@heroui/button';
import classnames from 'classnames';
import { useCallback, useState } from 'react';

import { ProgressionOption } from '@/types.ts';
import useProgression from '@/hooks/useProgression.ts';
import useAudioPlayer from '@/hooks/useAudioPlayer.ts';

interface ChordButtonProps {
  option: ProgressionOption;
  disabled?: boolean;
  selected?: boolean;
  active?: boolean;
  onClick?: () => void;
}

function ChordButton({ option, selected = false, active = false, disabled = false, onClick }: ChordButtonProps) {
  const { player } = useAudioPlayer();
  const { noteByOption, sampleSettingsByOption } = useProgression();
  const [isLoading, setIsLoading] = useState(false);

  const note = noteByOption(option);

  const handleClick = useCallback(() => {
    const { sample } = sampleSettingsByOption(option, note);

    if (player) {
      player.isSampleLoaded(sample).then((isLoaded) => {
        if (isLoaded) {
          player?.play(sample, { channel: 'chord' });
        } else {
          setIsLoading(true);
          player?.preloadAndPlay(sample, { channel: 'chord' })
            .then(() => {
              setIsLoading(false);
            });
        }
      });
    }

    if (onClick) {
      onClick();
    }
  }, [player, option, note, sampleSettingsByOption, onClick]);

  return (
    <Button
      className={classnames(
        'w-14 h-14 min-w-0 sm:w-16 sm:h-16 block p-0 rounded-lg relative border-1.5 transition-all',
        {
          'border-default-300': selected,
          'border-foreground': active,
          'opacity-60': disabled,
          'animate-pulse': isLoading,
        },
      )}
      color={selected ? 'primary' : 'default'}
      disabled={disabled || isLoading}
      size="lg"
      variant={selected ? 'solid' : 'faded'}
      onPress={handleClick}
    >
      <div
        className={classnames(
          'bg-default-300 absolute left-0 top-0 px-1 text-xs',
          { 'font-bold': option.chordQuality === 'major' },
        )}
      >
        {option.numeral}
      </div>
      <div className="flex justify-center items-end h-6">
        <div className="h-6 flex">
          {note.name}
        </div>
        {option.chordQuality === 'minor' && (
          <div className="text-sm flex items-end pb-[0.1em]">m</div>
        )}
        {option.chordQuality === 'dominant' && (
          <div className="text-xs flex h-6 items-start pl-[1px] pt-[2.5px]">7</div>
        )}
      </div>
    </Button>
  );
}

export default ChordButton;
